import type { TableColumn } from '@magicdoorinc/ui';
import type { MagicTagColumnRender } from '~/components/entity-magic-tags/EntityMagicTagsItems';
import type { CustomFieldDto } from '~/swagger/portal/Api';
import type { ViewColumnDto } from '~/swagger/settings/Api';

export enum SpecialCustomViewFieldName {
  AlignLeft = 'align-left',
  AlignRight = 'align-right',
}

export type SystemFieldKey = string | SpecialCustomViewFieldName;

export type Option<T = any> = TableColumn<T> & {
  systemFieldKey?: SystemFieldKey;
  type?: 'custom' | 'system';
  customField?: CustomFieldDto;
};

export class DynamicListViewItems<T> {
  constructor(
    private baseColumns: Option<T>[],
    private customFields: CustomFieldDto[] | undefined
  ) {
    this.lastColumn = this.baseColumns[this.baseColumns.length - 1];
  }

  private lastColumn: Option<T> | undefined;

  static sortAndFilterCustomFields = (customFields: CustomFieldDto[]) => {
    return customFields.sort((a, b) => b.order - a.order);
  };

  static matchColumn = (option: Option, column: ViewColumnDto) => {
    if (option.type === 'custom') {
      return option.customField?.id === column.customFieldId;
    }
    return option.systemFieldKey === column.key;
  };

  static getOptionKey = (option: Option) => {
    if (option.type === 'custom') {
      return option.customField?.id;
    }
    return option.systemFieldKey;
  };

  static isCustomColumnType = (type: string | undefined) => {
    return type?.toLowerCase() === 'custom';
  };

  static getColumnKey = (column: ViewColumnDto) => {
    if (DynamicListViewItems.isCustomColumnType(column.type)) {
      return column.customFieldId;
    }
    return column.key;
  };

  static isSpecialColumn = (column: Option) => {
    return (
      column.systemFieldKey &&
      ([SpecialCustomViewFieldName.AlignLeft, SpecialCustomViewFieldName.AlignRight] as SystemFieldKey[]).includes(column.systemFieldKey)
    );
  };

  static isCustomColumn = (column: Option) => {
    return column.type === 'custom';
  };

  getMagicTableItems = (customFields: CustomFieldDto[], render?: MagicTagColumnRender): Option<T>[] => {
    const _columns = DynamicListViewItems.sortAndFilterCustomFields(customFields).map((item) => {
      return {
        title: item.name,
        type: 'custom',
        customFieldId: item.id,
        render: render ? render(item) : undefined,

        customField: item,
      } as Option<T>;
    });

    return _columns.filter(Boolean);
  };

  getFullColumns = (opts?: { magicTagRender?: MagicTagColumnRender }) => {
    return this.baseColumns
      .slice(0, this.baseColumns.length - 1)
      .concat(this.getMagicTableItems(this.customFields || [], opts?.magicTagRender))
      .concat(this.lastColumn || []);
  };
}
